import { Overwrite } from "../utility-types"
import { ABTestExperiment, ExperimentId, ExperimentTag, SegmentId } from "./experiments"

type TestableExperimentTag<TExpId = ExperimentId> = Overwrite<ExperimentTag, { experimentId: TExpId }>

export const isValidExperiment = <TExpId = ExperimentId>(
    allowedIds: readonly TExpId[],
    tag: string[],
): tag is [ExperimentId, SegmentId] => {
    const [id, segment] = tag

    if (!id) return false
    if (!segment) return false
    if (!allowedIds.map((e) => e as string).includes(id)) return false
    if (!["a", "b"].includes(segment)) return false

    return true
}

export const encodeHistory = <TExpId = ExperimentId>(values: TestableExperimentTag<TExpId>[]): string =>
    values.map((e) => `${e.experimentId}||${e.segmentId}`).join("&&")

export const decodeHistory = <TExpId = ExperimentId>(
    allowedIds: readonly TExpId[],
    string: string,
): TestableExperimentTag<TExpId>[] => {
    return string
        .split("&&")
        .filter((x) => !!x)
        .map((r) => r.split("||", 2))
        .filter((x) => isValidExperiment(allowedIds, x))
        .map((x) => {
            return {
                experimentId: x[0] as TExpId,
                segmentId: x[1] as SegmentId,
            }
        })
}

export const updateHistory = <T = ExperimentId>(
    value: ExperimentTag<T>,
    enabledExperiments: readonly ABTestExperiment<T>[],
    history: ExperimentTag<T>[],
): ExperimentTag<T>[] => replaceOrAppendValue(cleanupHistory(history, enabledExperiments), value)

const cleanupHistory = <T>(
    history: ExperimentTag<T>[],
    enabledExperiments: readonly ABTestExperiment<T>[],
): ExperimentTag<T>[] => {
    const activeExperimentIds = enabledExperiments.map((e) => e.id)
    return history.filter((h) => activeExperimentIds.includes(h.experimentId))
}

const replaceOrAppendValue = <T>(history: ExperimentTag<T>[], value: ExperimentTag<T>) => [
    ...history.filter((x) => x.experimentId != value.experimentId),
    value,
]
